import { Component, EventEmitter, Output, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from 'src/app/services/auth.service';
import { ExamineeService } from 'src/app/services/examinee.service';
import { Examinee } from '../../models/examinee.model';
import { COUNTRY_PHONE_CODE_LIST } from '../../helpers/data';
import { Questionnaire } from '../../models/questionnaire.model';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { ExamineePerson } from '../../models/person.model';

@Component({
    selector: 'app-invite-examinee',
    templateUrl: './invite-examinee.component.html',
    styleUrl: './invite-examinee.component.scss',
    standalone: false
})
export class InviteExamineeComponent {
	@ViewChild('inviteExamineeModal') inviteExamineeModal!: TemplateRef<any>;
	@ViewChild('successModal') successModal!: TemplateRef<any>;
	@ViewChild('detailsErrorModal') detailsErrorModal!: TemplateRef<any>;
	@ViewChild('errorModal') errorModal!: TemplateRef<any>;

	@Output() examineeChanged: EventEmitter<any> = new EventEmitter();

	inviteForm!: UntypedFormGroup;
	submitted = false;

	reInvite = false;
	title = '';
	spinner = false;
	successMessage = '';
	success = false;
	questionnaireLoading = true;
	personLoading = false;
	fromQuestionnairePage = false;

	selectedExaminee!: Examinee | null;
	project_id!: number;
	selectedQuestionnaire!: Questionnaire | null;
	selectedPerson!: ExamineePerson;
	current_client!: any | null;
	inviteExamineeModalRef: any;

	countryCodeData = COUNTRY_PHONE_CODE_LIST;
	questionnaires: Questionnaire[] = [];
	persons: ExamineePerson[] = [];
	exist_person!: ExamineePerson;
	examinee_id!: number;

	detailsError: any = {
		title: '',
		message: '',
		update: false,
		resend: false
	}

	minExpiryDate!: Date;

	private questionnaireSearchSubject = new Subject<string>();
	private personSearchSubject = new Subject<string>();
	questionnaireSearchText: string = '';

	existPersonID = false;

	constructor(private modalService: NgbModal, private router: Router, private formBuilder: UntypedFormBuilder, private examineeService: ExamineeService, private authService: AuthService) {}

	get f() { return this.inviteForm.controls; }
	
	openModal(project_id: number, questionnaire?: Questionnaire, examinee?: Examinee) {
		this.persons = [];
		this.current_client = this.authService.currentClientValue;
		this.project_id = project_id;
		this.minExpiryDate = new Date();
		this.minExpiryDate.setDate(this.minExpiryDate.getDate() + 1);

		if (questionnaire) {
			this.selectedQuestionnaire = questionnaire;
			this.fromQuestionnairePage = true;
		} else {
			this.getQuestionnaireList();

			this.questionnaireSearchSubject.pipe(
				debounceTime(300), // Adjust debounce time as needed
				distinctUntilChanged()
			).subscribe(() => {
				this.getQuestionnaireList();
			});
		}

		this.submitted = false;
		this.spinner = false;
		this.success = false;

		if (examinee) {
			this.title = 'Resend Examinee Invitation';
			this.selectedExaminee = examinee;
			this.examinee_id = examinee.id;
			this.reInvite = true;

			this.inviteForm = this.formBuilder.group({
				gov_id: [{ value: this.selectedExaminee.person?.gov_id, disabled: true }, [Validators.required]],
				email: [{ value: this.selectedExaminee.person?.email, disabled: true }, [Validators.required, Validators.email]],
				phone: [{ value: this.selectedExaminee.person?.phone, disabled: true }, [Validators.required, Validators.minLength(6)]],
				phone_country_code: [{ value: this.selectedExaminee.person?.phone_country_code, disabled: true }, [Validators.required]],
				time_expiration: [null, [Validators.required]]
			});
		} else {
			this.title = 'Invite Examinee';
			this.inviteForm = this.formBuilder.group({
				gov_id: ['', [Validators.required]],
				email: ['', [Validators.required, Validators.email]],
				phone: ['', [Validators.required]],
				phone_country_code: [null, [Validators.required]],
				time_expiration: [null, [Validators.required]]
			});

			this.personSearchSubject.pipe(
				debounceTime(300), // Adjust debounce time as needed
				distinctUntilChanged()
			).subscribe(() => {
				this.getPersonList();
			});
		}

		if (this.authService.currentClientValue.plan) {
			this.inviteExamineeModalRef = this.modalService.open(this.inviteExamineeModal, { size: 'l', backdrop: 'static', keyboard : false });
		} else {
			this.inviteExamineeModalRef = this.modalService.open(this.errorModal, { size: 'l', backdrop: 'static', keyboard : false });
		}

	}

	getTooltipContent(): string {
    return this.persons.map(person => `${person.first_name ? person.first_name : person.email} ${person.last_name ? person.last_name : ''} (${person.gov_id})`).join('<br>');
  }

	getPersonList() {
		this.personLoading = true;
		
		this.examineeService.checkPersonIDExists(this.project_id, { searchText: this.f['gov_id'].value }).subscribe({
			next: (response) => {
				this.persons = response;
				this.personLoading = false;
			},
			error: (err) => {
				this.existPersonID = false;
				this.authService.toastErrorMessage(err.message ? err.message : err, 'Failed to fetch person ID!');
				this.personLoading = false;
			}
		});
	}

	getQuestionnaireList() {
		this.questionnaireLoading = true;
		
		this.examineeService.getProjectQuestionnairesList(this.project_id, { searchText: this.questionnaireSearchText }).subscribe({
			next: (response) => {
				this.questionnaires = response;
				this.questionnaireLoading = false;
			},
			error: (err) => {
				this.questionnaires = [];
				this.authService.toastErrorMessage(err.message ? err.message : err, 'Failed to fetch questionnaire list!');
				this.questionnaireLoading = false;
			}
		});
	}

	resendInvite() {
		this.submitted = true;
		this.spinner = true;
		
		this.examineeService.resendInviteProjectExaminee(this.project_id, this.examinee_id, { expired_at: this.inviteForm.value.time_expiration }).subscribe({
			next: (res) => {
				this.submitted = false;
				this.spinner = false;
				this.success = true;
				this.successMessage = res.message;

				this.inviteExamineeModalRef.close();
				this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
			},
			error: (err) => {
				this.success = false;
				this.submitted = false;
				this.spinner = false;
				this.authService.toastErrorMessage(err.message ? err.message : err , 'Resend Invitation!');
			}
		});
	}

	setInviteData(update?: number) {
		let data = {};

		if (update) {
			data = {
				person: {
					gov_id: this.inviteForm.value.gov_id,
					email: this.inviteForm.value.email,
					phone: this.inviteForm.value.phone,
					phone_country_code: this.inviteForm.value.phone_country_code,
				},
				expired_at: this.inviteForm.value.time_expiration,
				response: {
					update: update === 0 ? false : true
				}
			};
		} else {
			data = {
				person: {
					gov_id: this.inviteForm.value.gov_id,
					email: this.inviteForm.value.email,
					phone: this.inviteForm.value.phone,
					phone_country_code: this.inviteForm.value.phone_country_code,
				},
				expired_at: this.inviteForm.value.time_expiration
			};
		}

		return data;
	}

	onSubmit(update?: number) {
		this.submitted = true;
		this.spinner = true;

		this.detailsError.update = false;
		this.detailsError.resend = false;

		if (this.inviteForm.invalid) {
			this.spinner = false;
			return;
		}

		if (this.reInvite) {
			this.resendInvite();
		} else {
			const data = this.setInviteData(update);

			this.examineeService.inviteProjectExaminee(this.project_id, this.selectedQuestionnaire!.id, data).subscribe({
				next: (res) => {
					this.submitted = false;
					this.spinner = false;

					if (res.update) {
						this.inviteExamineeModalRef.close();
						this.detailsError.title = 'Update Details';
						this.detailsError.message = res.message;
						this.detailsError.update = true;
						this.exist_person = res.examinee_person;
						this.inviteExamineeModalRef = this.modalService.open(this.detailsErrorModal, { size: 'l', backdrop: 'static', keyboard : false });
					} else if (res.resend) {
						this.inviteExamineeModalRef.close();
						this.detailsError.title = 'Resend Invitation';
						this.detailsError.message = res.message;
						this.detailsError.resend = true;
						this.exist_person = res.examinee_person;
						this.examinee_id = res.examinee_id;
						this.inviteExamineeModalRef = this.modalService.open(this.detailsErrorModal, { size: 'l', backdrop: 'static', keyboard : false });
					} else {
						this.inviteExamineeModalRef.close();
						this.success = true;
						this.successMessage = res.message;
						this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
					}
				},
				error: (err) => {
					this.inviteExamineeModalRef.close();
					this.success = false;
					this.submitted = false;
					this.spinner = false;
					this.successMessage = err.message ? err.message : err;
					this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
				}
			});
		}
	}

	onSearchQuestionnaire(event: any) {
		this.questionnaireSearchText = event.target.value;
    this.questionnaireSearchSubject.next(event.target.value);
  }

	onSearchPerson() {
		if (this.f['gov_id'].value.length > 7) {
			this.personSearchSubject.next(this.f['gov_id'].value);
		}
  }

	closeModal() {
		if (this.current_client.plan) {
			if (this.router.url.includes('examinees')) {
				if (this.success) {
					this.examineeChanged.emit();
				}
			}

			if (this.router.url.includes('questionnaires')) {
				if (this.success) {
					this.examineeChanged.emit();
				}
			}

			this.reInvite = false;
			this.selectedExaminee = null;
			this.selectedQuestionnaire = null;

			this.detailsError.title = '';
			this.detailsError.message = '';
			this.detailsError.update = false;
			this.detailsError.resend = false;

			this.inviteForm.reset();
			this.modalService.dismissAll();
		} else {
			this.modalService.dismissAll();
		}
	}

	customSearchFn(term: any, item: any) {
		term = term.toLocaleLowerCase();
    return item.countryName.toLocaleLowerCase().indexOf(term) > -1
	}
}
